import React, { useState } from 'react';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import './LogoutModal.css';

/**
 * LogoutModal Component
 *
 * Shows a modal dialog asking user where to logout from:
 * - Current device only (default)
 * - All devices
 *
 * Enterprise-level logout control (TIER 4)
 */
function LogoutModal({ onConfirm, onCancel, isLoading }) {
  const [selectedScope, setSelectedScope] = useState('device'); // Default: device-level logout

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleConfirm = () => {
    onConfirm(selectedScope);
  };

  return (
    <div className="logout-modal-overlay">
      <div className="logout-modal-content">
        {/* Header */}
        <div className="logout-modal-header">
          <h2>👋 Confirm Logout</h2>
          <ModalCloseButton
            onClick={onCancel}
            disabled={isLoading}
            title="Cancel logout"
          />
        </div>

        {/* Body */}
        <div className="logout-modal-body">
          <p className="logout-modal-intro">
            Where would you like to logout from?
          </p>

          {/* Option 1: Device-level logout */}
          <div
            className={`logout-option ${selectedScope === 'device' ? 'selected' : ''}`}
            onClick={() => !isLoading && setSelectedScope('device')}
          >
            <input
              type="radio"
              id="logout-device"
              name="logout-scope"
              value="device"
              checked={selectedScope === 'device'}
              onChange={(e) => setSelectedScope(e.target.value)}
              disabled={isLoading}
            />
            <label htmlFor="logout-device" className="logout-option-label">
              <div className="logout-option-title">
                <span className="logout-option-icon">💻</span>
                <span className="logout-option-text">This Device Only</span>
              </div>
              <div className="logout-option-description">
                Logout from this browser and all its tabs. You'll stay logged in on other devices.
              </div>
            </label>
          </div>

          {/* Option 2: Global logout */}
          <div
            className={`logout-option ${selectedScope === 'all_devices' ? 'selected' : ''}`}
            onClick={() => !isLoading && setSelectedScope('all_devices')}
          >
            <input
              type="radio"
              id="logout-all"
              name="logout-scope"
              value="all_devices"
              checked={selectedScope === 'all_devices'}
              onChange={(e) => setSelectedScope(e.target.value)}
              disabled={isLoading}
            />
            <label htmlFor="logout-all" className="logout-option-label">
              <div className="logout-option-title">
                <span className="logout-option-icon">🌐</span>
                <span className="logout-option-text">All Devices</span>
              </div>
              <div className="logout-option-description">
                Logout from all devices and browsers. You'll need to login again everywhere.
              </div>
            </label>
          </div>

          {/* Security note */}
          <div className="logout-modal-note">
            <span className="note-icon">ℹ️</span>
            <span className="note-text">
              {selectedScope === 'device'
                ? 'You will be logged out from this browser immediately.'
                : 'All your active sessions will be terminated. You can still access on other devices after logging back in.'}
            </span>
          </div>
        </div>

        {/* Footer / Actions */}
        <div className="logout-modal-footer">
          <button
            type="button"
            className="action-btn action-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="action-btn action-primary"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-small"></span>
                Logging out...
              </>
            ) : (
              'Logout'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LogoutModal;
